import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { formatNumber, formatMoney, cn } from "@/lib/utils";
import { OrderStatus } from "@/config/status-registry";
import { Calendar } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";

export function OrderKanban({ items }: { items: any[] }) {
  const navigate = useNavigate();

  // Group items by status
  const grouped = items.reduce(
    (acc, item) => {
      const status = item.status || "DRAFT";
      if (!acc[status]) acc[status] = [];
      acc[status].push(item);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  return (
    <div className="flex h-full overflow-x-auto p-4 gap-4 items-start">
      {Object.entries(OrderStatus).map(([statusKey, config]) => {
        const columnItems = grouped[statusKey] || [];

        return (
          <div
            key={statusKey}
            className="flex-shrink-0 w-[300px] flex flex-col max-h-full bg-slate-100/50 rounded-xl border border-slate-200/60 overflow-hidden"
          >
            <div className="p-3 border-b border-slate-200/60 bg-white/50 backdrop-blur-sm sticky top-0 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", config.dotColor)} />
                <span className="font-semibold text-slate-700 text-sm">
                  {config.label}
                </span>
              </div>
              <span className="bg-white text-slate-500 text-xs px-2 py-0.5 rounded-full font-medium border border-slate-200 shadow-sm">
                {columnItems.length}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[150px]">
              {columnItems.map((item: any) => {
                const totalQty =
                  item.orderDetails?.reduce(
                    (sum: number, d: any) => sum + (d.quantity || 0),
                    0,
                  ) || 0;
                const deliveredQty =
                  item.orderDetails?.reduce(
                    (sum: number, d: any) => sum + (d.qtyDelivered || 0),
                    0,
                  ) || 0;
                const deliveryPct =
                  totalQty > 0
                    ? Math.round((deliveredQty / totalQty) * 100)
                    : 0;

                return (
                  <div
                    key={item.id || item.orderId}
                    onClick={() =>
                      navigate({
                        to: "/orders/$orderId",
                        params: { orderId: item.id || item.orderId } as any,
                      })
                    }
                    className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group active:scale-[0.98]"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-bold text-sm text-indigo-700 group-hover:text-indigo-600 transition-colors">
                        {item.orderId}
                      </span>
                      {item.orderType && (
                        <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase font-semibold">
                          {item.orderType}
                        </span>
                      )}
                    </div>

                    <div className="mb-2">
                      <div
                        className="font-medium text-slate-900 text-sm leading-tight line-clamp-1"
                        title={item.customer?.name || item.customerName}
                      >
                        {item.customer?.shortName ||
                          item.customer?.name ||
                          item.customerName ||
                          "—"}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                        {item.orderDetails
                          ?.map((d: any) => d.productName)
                          .join(", ") || "—"}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5 mt-3 pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">Giao hàng</span>
                        <span className="font-mono font-bold text-slate-700">
                          {formatNumber(deliveredQty)} /{" "}
                          {formatNumber(totalQty)}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden w-full">
                        <div
                          className={cn(
                            "h-full transition-all duration-500",
                            deliveryPct >= 100
                              ? "bg-emerald-500"
                              : "bg-amber-400",
                          )}
                          style={{ width: `${Math.min(deliveryPct, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-50">
                      <span className="font-mono text-[11px] text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {item.deliveryDeadline
                          ? new Date(item.deliveryDeadline).toLocaleDateString(
                              "vi-VN",
                            )
                          : "—"}
                      </span>
                      <span className="font-mono font-bold text-xs text-slate-900">
                        {formatMoney(item.totalAmount, item.currency)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {columnItems.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs py-8 border-2 border-dashed border-slate-200 rounded-lg">
                  Không có đơn hàng
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
